'use client';
import React, { useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Shield, Bell, Globe, CreditCard, Users, Lock, ChevronRight, Tag, Mail } from 'lucide-react';
import AdminPricingSection from '@/app/admin-dashboard/components/AdminPricingSection';
import EmailTemplatesSection from './components/EmailTemplatesSection';

interface SettingSection {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
}

const sections: SettingSection[] = [
  { id: 'general', icon: Globe, title: 'General', description: 'Business name, timezone, and regional settings' },
  { id: 'pricing', icon: Tag, title: 'Pricing', description: 'Service prices and add-on services management' },
  { id: 'email-templates', icon: Mail, title: 'Email Templates', description: 'Logo, brand colours, and dynamic content blocks for Resend emails' },
  { id: 'security', icon: Shield, title: 'Security', description: 'Password policy, 2FA, and access controls' },
  { id: 'notifications', icon: Bell, title: 'Notifications', description: 'Email and push notification preferences' },
  { id: 'billing', icon: CreditCard, title: 'Billing & Payments', description: 'Payment gateway, invoicing, and payout settings' },
  { id: 'team', icon: Users, title: 'Team & Roles', description: 'Manage admin users and permissions' },
  { id: 'privacy', icon: Lock, title: 'Privacy & Data', description: 'Data retention, GDPR, and privacy controls' },
];

export default function AdminSettingsPage() {
  const [activeSection, setActiveSection] = useState('general');

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your EzCleanCare platform settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar nav */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-border last:border-0 transition-colors ${activeSection === section.id ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}`}
                >
                  <section.icon size={16} className="flex-shrink-0" />
                  <span className="text-sm font-medium">{section.title}</span>
                  <ChevronRight size={14} className="ml-auto text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>

          {/* Content panel */}
          <div className="lg:col-span-3">
            <div className="bg-card rounded-2xl border border-border card-shadow p-6">
              {activeSection === 'general' && <GeneralSettings />}
              {activeSection === 'pricing' && <AdminPricingSection />}
              {activeSection === 'email-templates' && <EmailTemplatesSection />}
              {activeSection === 'security' && <SecuritySettings />}
              {activeSection === 'notifications' && <NotificationSettings />}
              {activeSection === 'billing' && <BillingSettings />}
              {activeSection === 'team' && <TeamSettings />}
              {activeSection === 'privacy' && <PrivacySettings />}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
      <div className="flex-1 pr-4">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ defaultChecked = false }: { defaultChecked?: boolean }) {
  const [on, setOn] = useState(defaultChecked);
  return (
    <button
      onClick={() => setOn(!on)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-primary' : 'bg-muted-foreground/30'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function GeneralSettings() {
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">General Settings</h2>
      <SettingRow label="Business Name" description="Displayed across the platform">
        <input defaultValue="EzCleanCare" className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-40" />
      </SettingRow>
      <SettingRow label="Timezone" description="Used for scheduling and reports">
        <select className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none w-40">
          <option>Europe/London</option>
          <option>Europe/Paris</option>
          <option>America/New_York</option>
        </select>
      </SettingRow>
      <SettingRow label="Currency" description="Default currency for pricing">
        <select className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none w-40">
          <option>GBP (£)</option>
          <option>EUR (€)</option>
          <option>USD ($)</option>
        </select>
      </SettingRow>
      <SettingRow label="Maintenance Mode" description="Temporarily disable customer bookings">
        <Toggle defaultChecked={false} />
      </SettingRow>
      <div className="mt-6">
        <button className="px-5 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all">Save Changes</button>
      </div>
    </div>
  );
}

function SecuritySettings() {
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Security Settings</h2>
      <SettingRow label="Two-Factor Authentication" description="Require 2FA for all admin accounts">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <SettingRow label="Session Timeout" description="Auto-logout after inactivity">
        <select className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none w-40">
          <option>30 minutes</option>
          <option>1 hour</option>
          <option>4 hours</option>
          <option>Never</option>
        </select>
      </SettingRow>
      <SettingRow label="Login Notifications" description="Email alert on new admin login">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <div className="mt-6">
        <button className="px-5 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all">Save Changes</button>
      </div>
    </div>
  );
}

function NotificationSettings() {
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Notification Settings</h2>
      <SettingRow label="New Booking Alerts" description="Notify when a customer books">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <SettingRow label="Complaint Alerts" description="Notify on new complaints">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <SettingRow label="Cleaner Check-in Alerts" description="Notify when a cleaner starts a job">
        <Toggle defaultChecked={false} />
      </SettingRow>
      <SettingRow label="Weekly Summary Email" description="Receive weekly performance digest">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <div className="mt-6">
        <button className="px-5 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all">Save Changes</button>
      </div>
    </div>
  );
}

function BillingSettings() {
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Billing & Payments</h2>
      <SettingRow label="Payment Gateway" description="Active payment processor">
        <span className="text-sm font-semibold text-foreground bg-success-bg text-success px-2.5 py-1 rounded-full">Stripe · Active</span>
      </SettingRow>
      <SettingRow label="Automatic Payouts" description="Auto-pay cleaners on job completion">
        <Toggle defaultChecked={false} />
      </SettingRow>
      <SettingRow label="Invoice Prefix" description="Prefix for generated invoices">
        <input defaultValue="CF-" className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-24" />
      </SettingRow>
      <div className="mt-6">
        <button className="px-5 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all">Save Changes</button>
      </div>
    </div>
  );
}

function TeamSettings() {
  const members = [
    { name: 'Admin User', email: 'admin@ezcleancare.io', role: 'Super Admin' },
    { name: 'Ops Manager', email: 'ops@ezcleancare.io', role: 'Manager' },
  ];
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Team & Roles</h2>
      <div className="space-y-3 mb-6">
        {members.map((m) => (
          <div key={m.email} className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/30">
            <div>
              <p className="text-sm font-semibold text-foreground">{m.name}</p>
              <p className="text-xs text-muted-foreground">{m.email}</p>
            </div>
            <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">{m.role}</span>
          </div>
        ))}
      </div>
      <button className="px-5 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors">+ Invite Team Member</button>
    </div>
  );
}

function PrivacySettings() {
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Privacy & Data</h2>
      <SettingRow label="Data Retention" description="How long to keep customer data">
        <select className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none w-40">
          <option>2 years</option>
          <option>5 years</option>
          <option>7 years</option>
          <option>Indefinitely</option>
        </select>
      </SettingRow>
      <SettingRow label="Cookie Consent Banner" description="Show GDPR cookie banner to visitors">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <SettingRow label="Analytics Tracking" description="Collect anonymised usage data">
        <Toggle defaultChecked={true} />
      </SettingRow>
      <div className="mt-6">
        <button className="px-5 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all">Save Changes</button>
      </div>
    </div>
  );
}
